import type { ReactNode } from 'react';
import * as React from 'react';

import EmbeddedUrlBlock from 'bundles/assess-common/components/EmbeddedUrlBlock';
import UploadedFileBlock from 'bundles/assess-common/components/UploadedFileBlock';
import {
  EmbeddedAudio,
  isEmbeddedAudio,
} from 'bundles/compound-assessments/components/shared/embeddedContent/EmbeddedAudio';
import {
  EmbeddedCodeBlock,
  isEmbeddedCodeBlock,
} from 'bundles/compound-assessments/components/shared/embeddedContent/EmbeddedCodeBlock';
import {
  EmbeddedImage,
  isEmbeddedImage,
} from 'bundles/compound-assessments/components/shared/embeddedContent/EmbeddedImage';
import { EmbeddedPdf, isEmbeddedPdf } from 'bundles/compound-assessments/components/shared/embeddedContent/EmbeddedPdf';
import {
  EmbeddedSoundCloud,
  isEmbeddedSoundCloud,
} from 'bundles/compound-assessments/components/shared/embeddedContent/EmbeddedSoundCloud';
import {
  EmbeddedVideo,
  isEmbeddedVideo,
} from 'bundles/compound-assessments/components/shared/embeddedContent/EmbeddedVideo';
import {
  EmbeddedVimeo,
  isEmbeddedVimeo,
} from 'bundles/compound-assessments/components/shared/embeddedContent/EmbeddedVimeo';
import {
  EmbeddedYoutube,
  isEmbeddedYoutube,
} from 'bundles/compound-assessments/components/shared/embeddedContent/EmbeddedYoutube';

export type EmbeddedContentProps = {
  url: string;
  title: string;
  renderFileUploadForPreview?: boolean;
};

const EmbeddedContent: React.FC<EmbeddedContentProps> = ({
  url,
  title,
  renderFileUploadForPreview,
}: EmbeddedContentProps) => {
  let content: ReactNode;

  if (isEmbeddedYoutube(url)) {
    content = <EmbeddedYoutube url={url} title={title} />;
  } else if (isEmbeddedSoundCloud(url)) {
    content = <EmbeddedSoundCloud url={url} title={title} />;
  } else if (isEmbeddedVimeo(url)) {
    content = <EmbeddedVimeo url={url} title={title} />;
  }
  // this piece of EmbeddedContent is only used for legacy and quick grader; the remaining
  // so we need to ensure the rendering of images are strictly controlled with a false check
  // this is to control removing the image render when an inline feedback rubric is in use
  // in quick grader
  else if (renderFileUploadForPreview !== false && isEmbeddedImage(url)) {
    content = <EmbeddedImage url={url} title={title} />;
  } else if (renderFileUploadForPreview && isEmbeddedVideo(url)) {
    content = <EmbeddedVideo url={url} title={title} />;
  } else if (renderFileUploadForPreview && isEmbeddedAudio(url)) {
    content = <EmbeddedAudio url={url} title={title} />;
  } else if (renderFileUploadForPreview && isEmbeddedPdf(url)) {
    content = <EmbeddedPdf url={url} title={title} />;
  } else if (renderFileUploadForPreview && isEmbeddedCodeBlock(url)) {
    content = <EmbeddedCodeBlock url={url} title={title} />;

    // file upload edit mode for this legacy compound-assessments actually uses the peer
    // bundle's upload, which covers the updated experience so we can explicitly disable=true
  } else if (renderFileUploadForPreview) {
    content = <UploadedFileBlock fileUrl={url} filename={title} disabled={true} />;
  } else {
    content = <EmbeddedUrlBlock url={url} urlTitle={title} disabled={true} />;
  }

  return <div>{content}</div>;
};

export default EmbeddedContent;
